import { Skeleton } from '@/components/ui/skeleton';
import { PageHeaderSkeleton } from './PageHeaderSkeleton';
import { PageWrapper, Container } from '@/components/layout';
import { Card, CardContent } from '@/components/ui/card';

/**
 * Skeleton for the VoteDetails page.
 * Reserves layout space to prevent CLS.
 *
 * Layout breakdown:
 * - PageHeader with breadcrumbs, actions, children (view/sort toggles): ~200px
 * - VoteOutcomeCard bar: ~60px
 * - 5-column vote summary grid: ~80px
 * - 2-column Yes/No grid with legislator rows: ~300px
 * - 3 stacked Present/Absent/Not Voting sections: ~200px
 */
export function VoteDetailsPageSkeleton() {
  return (
    <PageWrapper
      header={
        <PageHeaderSkeleton
          showBreadcrumbs={true}
          showDescription={true}
          showActions={true}
          childrenHeight={52}
        />
      }
    >
      <Container padding size="2xl">
        <div className="space-y-6">
          <Card>
            <CardContent className="pt-6">
              {/* Vote outcome bar */}
              <Skeleton className="h-16 w-full mb-6 rounded-lg" />

              {/* Vote summary panel — 5 columns */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2 sm:gap-4 mb-4 sm:mb-6">
                {['Yes', 'No', 'Present', 'Absent', 'Not Voting'].map((label) => (
                  <div key={label} className="p-2 sm:p-4 rounded-lg border border-border">
                    <Skeleton className="h-4 w-16 mb-2" />
                    <Skeleton className="h-5 w-full" />
                  </div>
                ))}
              </div>

              {/* Yes/No 2-col grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-6">
                {['Yes', 'No'].map((label) => (
                  <div key={label} className="p-3 sm:p-4 rounded-lg border border-border">
                    <Skeleton className="h-5 w-12 mb-3 sm:mb-4" />
                    <div className="space-y-2">
                      {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <Skeleton className="h-8 w-8 rounded-full flex-shrink-0" />
                          <Skeleton className="h-4 w-32" />
                          <Skeleton className="h-4 w-12 ml-auto" />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Present/Absent/Not Voting stacked */}
              <div className="mt-4 sm:mt-6 space-y-3 sm:space-y-4">
                {['Present', 'Absent', 'Not Voting'].map((label) => (
                  <div key={label} className="p-3 sm:p-4 rounded-lg border border-border">
                    <Skeleton className="h-5 w-24 mb-3 sm:mb-4" />
                    <div className="space-y-2">
                      {Array.from({ length: 2 }).map((_, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <Skeleton className="h-8 w-8 rounded-full flex-shrink-0" />
                          <Skeleton className="h-4 w-28" />
                          <Skeleton className="h-4 w-12 ml-auto" />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </Container>
    </PageWrapper>
  );
}
